import { useState, useEffect, useCallback } from "react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Package, Truck, IndianRupee, AlertTriangle, Users, Shield, Settings, BookOpen, Map, BarChart3, FileText, Download, Search, Plus, ChevronRight, ChevronDown, X, Check, Clock, RefreshCw, Eye, Edit, Trash2, Phone, Mail, Home, LogOut, Menu, Bell, ArrowLeft, ExternalLink, Filter } from "lucide-react";

// ═══════════════════════════════════════════════════════════════
// SHIPSWIFT COMPLETE FRONTEND — All Pages
// India Post Heritage Palette + Responsive
// ═══════════════════════════════════════════════════════════════

const API = "/api/v1";
const C = {
  bg: "#FDFBF7", surface: "#FFFFFF", surface2: "#F5F2ED", border: "#E0DCD5",
  text: "#1A1A1A", text2: "#555", text3: "#999",
  accent: "#C41E3A", accentLight: "#FDF2F4",
  green: "#138808", greenBg: "#EEFBE8",
  blue: "#1E40AF", blueBg: "#EFF6FF",
  gold: "#D4A843", goldBg: "#FBF5E8",
  red: "#C41E3A", redBg: "#FDF2F4",
  navy: "#1A1F3A",
};

// ── API Helper ──
const api = async (path, opts = {}) => {
  const token = localStorage.getItem("shipswift_jwt");
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) { localStorage.removeItem("token"); window.location.reload(); }
  if (res.status === 204) return null;
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
};

// ── Shared Components ──
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>{children}</div>
);
const Stat = ({ icon: Icon, label, value, sub, color = C.navy }) => (
  <Card className="p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: color + "15" }}>
        <Icon size={20} style={{ color }} />
      </div>
      <span className="text-sm text-gray-500">{label}</span>
    </div>
    <div className="text-2xl font-bold" style={{ color }}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </Card>
);
const Badge = ({ children, color = C.blue }) => (
  <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: color + "20", color }}>{children}</span>
);
const Btn = ({ children, onClick, variant = "primary", size = "md", disabled, className = "" }) => {
  const base = "rounded-lg font-medium transition-all inline-flex items-center gap-2 disabled:opacity-50";
  const sizes = { sm: "px-3 py-1.5 text-sm", md: "px-4 py-2 text-sm", lg: "px-6 py-3" };
  const variants = {
    primary: `bg-[${C.accent}] text-white hover:opacity-90`,
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    danger: "bg-red-50 text-red-700 hover:bg-red-100",
    ghost: "text-gray-600 hover:bg-gray-100",
  };
  return <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} onClick={onClick} disabled={disabled} style={variant === "primary" ? { background: C.accent, color: "#fff" } : {}}>{children}</button>;
};
const Input = ({ label, ...props }) => (
  <div className="mb-3">
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" {...props} />
  </div>
);
const Select = ({ label, options, ...props }) => (
  <div className="mb-3">
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" {...props}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
const Empty = ({ icon: Icon, title, sub }) => (
  <div className="text-center py-16 text-gray-400">
    <Icon size={40} className="mx-auto mb-3 opacity-40" />
    <div className="font-medium text-gray-500">{title}</div>
    {sub && <div className="text-sm mt-1">{sub}</div>}
  </div>
);
const Loading = () => <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-gray-400" size={24} /></div>;
const paise = v => `₹${(v / 100).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

// ═══════════════════════════════════════════════════════════════
// PAGE: ADMIN PANEL
// ═══════════════════════════════════════════════════════════════
const AdminPanel = () => {
  const [tab, setTab] = useState("kyc");
  const [merchants, setMerchants] = useState([]);
  const [kycPending, setKycPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [mList, kycList] = await Promise.all([
          api("/admin/merchants?page=1&page_size=50"),
          api("/admin/merchants/kyc-pending?page=1&page_size=50"),
        ]);
        setMerchants(mList.merchants || []);
        setKycPending(kycList.merchants || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const handleKYC = async (id, action) => {
    try {
      await api(`/admin/merchants/${id}/kyc`, { method: "POST", body: { action, notes: `${action} via admin panel` } });
      setKycPending(prev => prev.filter(m => m.id !== id));
    } catch (e) { alert(e.message); }
  };

  const handleSuspend = async (id) => {
    if (!confirm("Suspend this merchant?")) return;
    try {
      await api(`/admin/merchants/${id}/suspend`, { method: "POST", body: { reason: "Admin action" } });
      alert("Merchant suspended");
    } catch (e) { alert(e.message); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6" style={{ color: C.navy }}>Admin Panel</h1>
      <div className="flex gap-2 mb-6 border-b">
        {[["kyc", "KYC Queue", Shield], ["merchants", "All Merchants", Users], ["cod", "COD Monitor", IndianRupee]].map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${tab === k ? "border-red-600 text-red-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            <I size={16} />{l}{k === "kyc" && kycPending.length > 0 && <Badge color={C.red}>{kycPending.length}</Badge>}
          </button>
        ))}
      </div>

      {tab === "kyc" && (
        <div className="space-y-3">
          {kycPending.length === 0 ? <Empty icon={Check} title="No pending KYC" sub="All merchants are verified" /> :
            kycPending.map(m => (
              <Card key={m.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{m.business_name}</div>
                  <div className="text-sm text-gray-500">{m.email} · {m.phone}</div>
                  <div className="text-xs text-gray-400 mt-1">GSTIN: {m.gstin || "N/A"} · Registered: {m.created_at?.split("T")[0]}</div>
                </div>
                <div className="flex gap-2">
                  <Btn size="sm" onClick={() => handleKYC(m.id, "approve")}><Check size={14} /> Approve</Btn>
                  <Btn size="sm" variant="danger" onClick={() => handleKYC(m.id, "reject")}><X size={14} /> Reject</Btn>
                </div>
              </Card>
            ))}
        </div>
      )}

      {tab === "merchants" && (
        <div className="space-y-3">
          {merchants.map(m => (
            <Card key={m.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{m.business_name} <Badge color={m.status === "active" ? C.green : C.red}>{m.status}</Badge></div>
                <div className="text-sm text-gray-500">{m.email} · Plan: {m.plan}</div>
              </div>
              <div className="flex gap-2">
                <Btn size="sm" variant="ghost"><Eye size={14} /> View</Btn>
                {m.status === "active" && <Btn size="sm" variant="danger" onClick={() => handleSuspend(m.id)}>Suspend</Btn>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "cod" && <CODMonitor />}
    </div>
  );
};

const CODMonitor = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api("/admin/cod-monitor").then(setData).catch(console.error); }, []);
  if (!data) return <Loading />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Stat icon={IndianRupee} label="COD Collected" value={paise(data.total_collected_paise || 0)} color={C.green} />
      <Stat icon={IndianRupee} label="COD Settled" value={paise(data.total_settled_paise || 0)} color={C.blue} />
      <Stat icon={AlertTriangle} label="Pending Settlement" value={paise(data.total_pending_paise || 0)} color={C.gold} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: DISPUTES
// ═══════════════════════════════════════════════════════════════
const DisputesPage = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFile, setShowFile] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/disputes?status=${filter}` : "/disputes";
      setDisputes(await api(url));
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const statusColor = { open: C.blue, investigating: C.gold, escalated: C.accent, resolved: C.green, closed: C.text3 };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: C.navy }}>Disputes</h1>
        <Btn onClick={() => setShowFile(true)}><Plus size={16} /> File Dispute</Btn>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "open", "investigating", "escalated", "resolved", "closed"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {s || "All"}
          </button>
        ))}
      </div>
      {loading ? <Loading /> : disputes.length === 0 ? <Empty icon={Shield} title="No disputes" sub="All shipments are clean" /> : (
        <div className="space-y-3">
          {disputes.map(d => (
            <Card key={d.id} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold">{d.dispute_number}</span>
                  <Badge color={statusColor[d.status] || C.text3}>{d.status}</Badge>
                  <Badge color={C.navy}>{d.dispute_type.replace(/_/g, " ")}</Badge>
                </div>
                <span className="text-sm text-gray-400">{d.filed_at?.split("T")[0]}</span>
              </div>
              <p className="text-sm text-gray-600">{d.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span>Claimed: {paise(d.claimed_amount_paise)}</span>
                {d.resolved_amount_paise > 0 && <span>Resolved: {paise(d.resolved_amount_paise)}</span>}
                {d.sla_deadline && <span>SLA: {d.sla_deadline.split("T")[0]}</span>}
                <span>{d.evidence?.length || 0} evidence · {d.notes?.length || 0} notes</span>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showFile && <FileDisputeModal onClose={() => { setShowFile(false); load(); }} />}
    </div>
  );
};

const FileDisputeModal = ({ onClose }) => {
  const [form, setForm] = useState({ order_id: "", dispute_type: "damaged", description: "", claimed_amount_paise: 0 });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api("/disputes", { method: "POST", body: form });
      onClose();
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">File Dispute</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <Input label="Order ID" value={form.order_id} onChange={e => set("order_id", e.target.value)} placeholder="Paste order ID" />
        <Select label="Type" value={form.dispute_type} onChange={e => set("dispute_type", e.target.value)} options={[
          { value: "damaged", label: "Damaged" }, { value: "lost_in_transit", label: "Lost in Transit" },
          { value: "delivered_not_received", label: "Delivered Not Received" }, { value: "weight_discrepancy", label: "Weight Discrepancy" },
          { value: "overcharge", label: "Overcharge" }, { value: "delay", label: "Delay" }, { value: "other", label: "Other" },
        ]} />
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (min 10 chars)</label>
          <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={3} value={form.description} onChange={e => set("description", e.target.value)} />
        </div>
        <Input label="Claimed Amount (₹)" type="number" value={form.claimed_amount_paise / 100} onChange={e => set("claimed_amount_paise", Math.round(e.target.value * 100))} />
        <div className="flex gap-2 mt-4">
          <Btn onClick={submit} disabled={submitting || form.description.length < 10}>Submit Dispute</Btn>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        </div>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: NDR MANAGEMENT
// ═══════════════════════════════════════════════════════════════
const NDRPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const d = await api("/orders/ndr"); setOrders(d.orders || []); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleAction = async (orderId, action) => {
    try {
      await api(`/orders/${orderId}/ndr-action`, { method: "POST", body: { action } });
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: C.navy }}>NDR Management</h1>
        <Btn variant="secondary" onClick={load}><RefreshCw size={16} /> Refresh</Btn>
      </div>
      {loading ? <Loading /> : orders.length === 0 ? <Empty icon={Truck} title="No NDR orders" sub="All deliveries are on track" /> : (
        <div className="space-y-3">
          {orders.map(o => (
            <Card key={o.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-sm font-bold">{o.order_number} <Badge color={C.red}>NDR</Badge></div>
                  <div className="text-sm text-gray-500 mt-1">{o.receiver_name} · {o.receiver_city} · {o.receiver_pincode}</div>
                  <div className="text-xs text-gray-400 mt-1">AWB: {o.awb} · {o.payment_mode === "cod" ? `COD ${paise(o.cod_amount_paise)}` : "Prepaid"}</div>
                </div>
                <div className="flex gap-2">
                  <Btn size="sm" onClick={() => handleAction(o.id, "reattempt")}><RefreshCw size={14} /> Reattempt</Btn>
                  <Btn size="sm" variant="danger" onClick={() => handleAction(o.id, "rto")}><ArrowLeft size={14} /> RTO</Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: SETTINGS
// ═══════════════════════════════════════════════════════════════
const SettingsPage = () => {
  const [tab, setTab] = useState("webhook");
  const [webhook, setWebhook] = useState({ url: "", secret: "", events: [] });
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [wh, keys] = await Promise.all([
          api("/webhooks/config").catch(() => ({ url: "", secret: "" })),
          api("/api-keys").catch(() => ({ keys: [] })),
        ]);
        setWebhook(wh);
        setApiKeys(keys.keys || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const saveWebhook = async () => {
    try {
      await api("/webhooks/config", { method: "PUT", body: webhook });
      alert("Webhook saved");
    } catch (e) { alert(e.message); }
  };

  const createKey = async () => {
    const label = prompt("API key label:");
    if (!label) return;
    try {
      const key = await api("/api-keys", { method: "POST", body: { label, scope: "full" } });
      alert(`Key created: ${key.key}\n\nCopy it now — it won't be shown again.`);
      setApiKeys(prev => [...prev, key]);
    } catch (e) { alert(e.message); }
  };

  const revokeKey = async (id) => {
    if (!confirm("Revoke this API key?")) return;
    try {
      await api(`/api-keys/${id}`, { method: "DELETE" });
      setApiKeys(prev => prev.filter(k => k.id !== id));
    } catch (e) { alert(e.message); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6" style={{ color: C.navy }}>Settings</h1>
      <div className="flex gap-2 mb-6 border-b">
        {[["webhook", "Webhooks"], ["apikeys", "API Keys"], ["notifications", "Notifications"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-3 text-sm font-medium border-b-2 ${tab === k ? "border-red-600 text-red-700" : "border-transparent text-gray-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "webhook" && (
        <Card className="p-6 max-w-xl">
          <h2 className="font-semibold mb-4">Webhook Configuration</h2>
          <Input label="Webhook URL" value={webhook.url || ""} onChange={e => setWebhook(p => ({ ...p, url: e.target.value }))} placeholder="https://your-server.com/webhook" />
          <Input label="Webhook Secret" value={webhook.secret || ""} onChange={e => setWebhook(p => ({ ...p, secret: e.target.value }))} placeholder="Auto-generated if empty" />
          <p className="text-xs text-gray-400 mb-4">Events are signed with HMAC-SHA256. Verify the X-ShipSwift-Signature header.</p>
          <Btn onClick={saveWebhook}>Save Webhook</Btn>
        </Card>
      )}

      {tab === "apikeys" && (
        <div>
          <Btn onClick={createKey} className="mb-4"><Plus size={16} /> Generate API Key</Btn>
          <div className="space-y-3">
            {apiKeys.length === 0 ? <Empty icon={Settings} title="No API keys" sub="Create one to integrate via API" /> :
              apiKeys.map(k => (
                <Card key={k.id} className="p-4 flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{k.label} <Badge>{k.scope}</Badge></div>
                    <div className="text-xs text-gray-400 font-mono mt-1">{k.key_prefix || k.id}...</div>
                  </div>
                  <Btn size="sm" variant="danger" onClick={() => revokeKey(k.id)}><Trash2 size={14} /> Revoke</Btn>
                </Card>
              ))}
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <Card className="p-6 max-w-xl">
          <h2 className="font-semibold mb-4">Notification Preferences</h2>
          <p className="text-sm text-gray-500 mb-4">Buyer notifications are configured at the channel level. Contact your channel admin to customize SMS/WhatsApp templates.</p>
          <div className="space-y-3">
            {["Order booked", "Shipped / In transit", "Out for delivery", "Delivered", "NDR / Failed delivery", "RTO initiated"].map(e => (
              <label key={e} className="flex items-center gap-3 text-sm">
                <input type="checkbox" defaultChecked className="rounded" />
                <span>{e}</span>
              </label>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: ANALYTICS
// ═══════════════════════════════════════════════════════════════
const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [summary, timeseries] = await Promise.all([
          api("/analytics/me/summary").catch(() => ({ total_orders: 0, total_revenue_paise: 0, delivered_orders: 0, rto_orders: 0, avg_delivery_days: 0 })),
          api("/analytics/traffic/timeseries?days=14").catch(() => []),
        ]);
        setData({ summary, timeseries });
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <Loading />;
  const s = data?.summary || {};

  const chartData = (data?.timeseries || []).map(t => ({ date: t.date?.slice(5), visits: t.page_views || 0, orders: t.orders || 0 }));
  const deliveryRate = s.total_orders > 0 ? ((s.delivered_orders / s.total_orders) * 100).toFixed(1) : 0;
  const rtoRate = s.total_orders > 0 ? ((s.rto_orders / s.total_orders) * 100).toFixed(1) : 0;
  const pieData = [
    { name: "Delivered", value: s.delivered_orders || 0, color: C.green },
    { name: "RTO", value: s.rto_orders || 0, color: C.red },
    { name: "In Transit", value: Math.max(0, (s.total_orders || 0) - (s.delivered_orders || 0) - (s.rto_orders || 0)), color: C.blue },
  ].filter(d => d.value > 0);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6" style={{ color: C.navy }}>Analytics</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Package} label="Total Orders" value={s.total_orders || 0} color={C.navy} />
        <Stat icon={IndianRupee} label="Revenue" value={paise(s.total_revenue_paise || 0)} color={C.green} />
        <Stat icon={Truck} label="Delivery Rate" value={`${deliveryRate}%`} color={C.green} />
        <Stat icon={AlertTriangle} label="RTO Rate" value={`${rtoRate}%`} color={rtoRate > 15 ? C.red : C.gold} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold mb-4">Orders & Traffic (14 days)</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="orders" stroke={C.accent} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="visits" stroke={C.blue} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty icon={BarChart3} title="No data yet" sub="Analytics populate after your first orders" />}
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold mb-4">Order Outcomes</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty icon={PieChart} title="No orders yet" />}
        </Card>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: ADDRESS BOOK
// ═══════════════════════════════════════════════════════════════
const AddressBookPage = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = search ? `/address-book?search=${search}` : "/address-book";
      setRecipients(await api(url));
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [search]);

  const remove = async (id) => {
    if (!confirm("Delete this recipient?")) return;
    try { await api(`/address-book/${id}`, { method: "DELETE" }); load(); } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: C.navy }}>Address Book</h1>
        <Btn onClick={() => setShowAdd(true)}><Plus size={16} /> Add Recipient</Btn>
      </div>
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-3 text-gray-400" />
        <input className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm" placeholder="Search by name, phone, city, or pincode..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? <Loading /> : recipients.length === 0 ? <Empty icon={BookOpen} title="No recipients" sub={search ? "No matches found" : "Add your frequently used addresses"} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {recipients.map(r => (
            <Card key={r.id} className="p-4">
              <div className="flex justify-between">
                <div>
                  <div className="font-semibold">{r.name} {r.label && <Badge color={C.gold}>{r.label}</Badge>}</div>
                  <div className="text-sm text-gray-500 mt-1">{r.address_line1}, {r.city}, {r.state} - {r.pincode}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span><Phone size={11} className="inline" /> {r.phone}</span>
                    {r.company && <span>{r.company}</span>}
                    <span>Used {r.use_count}x</span>
                  </div>
                </div>
                <button onClick={() => remove(r.id)} className="text-gray-300 hover:text-red-500 self-start"><Trash2 size={16} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showAdd && <AddRecipientModal onClose={() => { setShowAdd(false); load(); }} />}
    </div>
  );
};

const AddRecipientModal = ({ onClose }) => {
  const [f, setF] = useState({ name: "", phone: "", address_line1: "", city: "", state: "", pincode: "", label: "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = async () => {
    try { await api("/address-book", { method: "POST", body: f }); onClose(); } catch (e) { alert(e.message); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Add Recipient</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <Input label="Name" value={f.name} onChange={e => set("name", e.target.value)} />
        <Input label="Phone" value={f.phone} onChange={e => set("phone", e.target.value)} />
        <Input label="Address" value={f.address_line1} onChange={e => set("address_line1", e.target.value)} />
        <div className="grid grid-cols-3 gap-2">
          <Input label="City" value={f.city} onChange={e => set("city", e.target.value)} />
          <Input label="State" value={f.state} onChange={e => set("state", e.target.value)} />
          <Input label="Pincode" value={f.pincode} onChange={e => set("pincode", e.target.value)} />
        </div>
        <Input label="Label (optional)" value={f.label} onChange={e => set("label", e.target.value)} placeholder="e.g. Mom, Warehouse B" />
        <Btn onClick={submit} className="mt-2">Save Recipient</Btn>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: CHANNEL ADMIN
// ═══════════════════════════════════════════════════════════════
const ChannelAdminPage = () => {
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, m] = await Promise.all([
          api("/channels/me/stats"),
          api("/channels/me/merchants"),
        ]);
        setStats(s);
        setMerchants(m.merchants || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const exportMIS = async (format) => {
    try {
      const token = localStorage.getItem("shipswift_jwt");
      const res = await fetch(`${API}/channels/me/export?format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `MIS_export.${format}`; a.click();
    } catch (e) { alert(e.message); }
  };

  if (loading) return <Loading />;
  if (!stats) return <Empty icon={Users} title="Not a channel admin" sub="You don't have channel admin permissions" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: C.navy }}>Channel Dashboard</h1>
        <div className="flex gap-2">
          <Btn variant="secondary" size="sm" onClick={() => exportMIS("csv")}><Download size={14} /> CSV</Btn>
          <Btn variant="secondary" size="sm" onClick={() => exportMIS("json")}><Download size={14} /> JSON</Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Total Merchants" value={stats.total_merchants} color={C.navy} />
        <Stat icon={Users} label="Active" value={stats.active_merchants} color={C.green} />
        <Stat icon={Package} label="Total Orders" value={stats.total_orders} color={C.blue} />
        <Stat icon={IndianRupee} label="Revenue" value={paise(stats.total_revenue_paise)} color={C.green} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Stat icon={Truck} label="Delivered" value={stats.delivered_orders} sub={`${stats.delivery_rate_pct}%`} color={C.green} />
        <Stat icon={AlertTriangle} label="RTO" value={stats.rto_orders} sub={`${stats.rto_rate_pct}%`} color={C.red} />
        <Stat icon={IndianRupee} label="Margin" value={paise(stats.total_margin_paise)} color={C.gold} />
      </div>

      <h2 className="text-lg font-semibold mb-3">Channel Merchants</h2>
      <div className="space-y-2">
        {merchants.map(m => (
          <Card key={m.id} className="p-3 flex justify-between items-center">
            <div>
              <span className="font-medium">{m.business_name}</span>
              <span className="text-sm text-gray-400 ml-2">{m.email}</span>
            </div>
            <Badge color={m.status === "active" ? C.green : C.red}>{m.status}</Badge>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PAGE: BULK LABELS
// ═══════════════════════════════════════════════════════════════
const BulkLabelsPage = () => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/orders?limit=50").then(d => { setOrders((d.orders || []).filter(o => o.awb)); setLoading(false); }).catch(console.error);
  }, []);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(orders.map(o => o.id)));
  const clearAll = () => setSelected(new Set());

  const download = async () => {
    if (selected.size === 0) return;
    try {
      const token = localStorage.getItem("shipswift_jwt");
      const res = await fetch(`${API}/bulk-orders/labels/bulk`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_ids: [...selected], format: "pdf" }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `labels_${selected.size}.pdf`; a.click();
    } catch (e) { alert(e.message); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: C.navy }}>Bulk Labels</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{selected.size} selected</span>
          <Btn variant="secondary" size="sm" onClick={selectAll}>Select All</Btn>
          <Btn variant="secondary" size="sm" onClick={clearAll}>Clear</Btn>
          <Btn size="sm" onClick={download} disabled={selected.size === 0}><Download size={14} /> Download PDF</Btn>
        </div>
      </div>
      {orders.length === 0 ? <Empty icon={FileText} title="No booked orders" sub="Book orders to generate labels" /> : (
        <div className="space-y-2">
          {orders.map(o => (
            <Card key={o.id} className={`p-3 flex items-center gap-3 cursor-pointer transition-all ${selected.has(o.id) ? "ring-2 ring-red-300 bg-red-50/30" : "hover:bg-gray-50"}`} onClick={() => toggle(o.id)}>
              <input type="checkbox" checked={selected.has(o.id)} readOnly className="rounded" />
              <div className="flex-1">
                <span className="font-mono text-sm font-bold">{o.order_number}</span>
                <span className="text-sm text-gray-500 ml-3">{o.receiver_name} · {o.receiver_city}</span>
              </div>
              <span className="text-xs font-mono text-gray-400">{o.awb}</span>
              <Badge color={C.green}>{o.status}</Badge>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN APP — NAVIGATION + ROUTING
// ═══════════════════════════════════════════════════════════════
const NAV = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "orders", label: "Orders", icon: Package },
  { key: "ndr", label: "NDR", icon: AlertTriangle },
  { key: "disputes", label: "Disputes", icon: Shield },
  { key: "labels", label: "Bulk Labels", icon: FileText },
  { key: "addressbook", label: "Address Book", icon: BookOpen },
  { key: "channel", label: "Channel Admin", icon: Users },
  { key: "settings", label: "Settings", icon: Settings },
  { key: "admin", label: "Admin Panel", icon: Shield },
];

export default function ShipSwiftApp() {
  const [page, setPage] = useState("analytics");
  const [menuOpen, setMenuOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api("/merchants/me").then(setProfile).catch(() => {});
  }, []);

  const Page = {
    analytics: AnalyticsPage,
    disputes: DisputesPage,
    ndr: NDRPage,
    settings: SettingsPage,
    addressbook: AddressBookPage,
    channel: ChannelAdminPage,
    admin: AdminPanel,
    labels: BulkLabelsPage,
    orders: () => <Empty icon={Package} title="Orders" sub="Use the main ShipSwift dashboard for order management" />,
  }[page] || AnalyticsPage;

  return (
    <div className="min-h-screen" style={{ background: C.bg }}>
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)}><Menu size={22} /></button>
          <span className="text-xl font-bold" style={{ color: C.accent }}>📮 ShipSwift</span>
        </div>
        <div className="flex items-center gap-4">
          {profile && <span className="text-sm text-gray-500 hidden md:block">{profile.business_name}</span>}
          <button className="text-gray-400 hover:text-gray-600"><Bell size={20} /></button>
          <button onClick={() => { localStorage.removeItem("token"); window.location.reload(); }} className="text-gray-400 hover:text-red-500"><LogOut size={20} /></button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className={`fixed md:sticky top-[57px] left-0 h-[calc(100vh-57px)] w-60 bg-white border-r border-gray-200 overflow-y-auto z-30 transition-transform ${menuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
          <div className="py-2">
            {NAV.map(n => (
              <button key={n.key} onClick={() => { setPage(n.key); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${page === n.key ? "bg-red-50 text-red-700 font-semibold border-r-2 border-red-600" : "text-gray-600 hover:bg-gray-50"}`}>
                <n.icon size={18} />{n.label}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-100 px-4 py-3 text-xs text-gray-400">
            <a href="/terms" target="_blank" className="hover:underline">Terms</a> · <a href="/privacy" target="_blank" className="hover:underline">Privacy</a> · <a href="/refund-policy" target="_blank" className="hover:underline">Refund</a>
          </div>
        </nav>

        {/* Overlay for mobile menu */}
        {menuOpen && <div className="fixed inset-0 bg-black/30 z-20 md:hidden" onClick={() => setMenuOpen(false)} />}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 min-h-[calc(100vh-57px)]">
          <Page />
        </main>
      </div>
    </div>
  );
}
